import type { CSSProperties } from "react";
import { useLocale, useT } from "../settings/context";
import { choiceNumber } from "./smsVote";
import { percentLabels, tallyRows } from "./tally";
import {
  answerTimeline,
  confetti,
  podiumOf,
  revealAt,
  readReveal,
  tileColumns,
  verdictOf,
  winnerAt,
  ANSWER_TIMING,
  TIMING,
  VERDICT_TIMING,
  type AnswerReveal,
  type PodiumReveal,
  type VerdictReveal,
} from "./reveal";
import type { Activity, LivePayload, Results as ResultsData } from "./types";

/**
 * ═══ LES RÉSULTATS ═══
 *
 * Trois présentations, reprises de la V1 (`Bars`, `Gauge`, `Pie`), sur une seule
 * source de nombres (`tally.ts`), la réponse la plus choisie en tête. Et une
 * révélation animée, réglée sur l'activité (voir `reveal.ts`) : le podium, la bonne
 * réponse d'un quiz, le verdict d'un vote.
 *
 * ⚠️ LE POURCENTAGE SE CALCULE À L'ÉCRAN, PAS AU SERVEUR, mais à partir de ce que le
 * serveur a bien voulu envoyer. Quand l'organisateur a demandé « les résultats
 * sans le nombre de votants », le serveur n'envoie QUE des parts : le compte n'est
 * pas caché à l'affichage, il n'est pas dans le message. C'est la différence entre
 * masquer et ne pas dire.
 */

/** LE NUMÉRO D'UNE RÉPONSE, celui qu'on envoie par SMS (voir `smsVote.ts`). */
export function SmsNumber({ index }: { index: number }) {
  return <span className="smsn">{choiceNumber(index)}</span>;
}

export function Results({
  live,
  mine = [],
  numbers = false,
}: {
  live: LivePayload;
  mine?: number[];
  /**
   * Chaque réponse porte son numéro de vote par SMS — réception ouverte, sur le mur
   * comme sur le téléphone.
   * C'est à lui qu'on retrouve, dans les résultats, la réponse qu'on a envoyée.
   */
  numbers?: boolean;
}) {
  const activity = live.activity;
  const raw = live.results;
  if (!activity || !raw) return null;

  /**
   * ⚠️ CE QUE VOIT LE PUBLIC MASQUE TOUJOURS, QUI QUE SOIT LE LECTEUR. Le serveur
   * retire les chiffres aux participants quand « montrer les parts sans le nombre de
   * votants » est coché, mais il les LAISSE à l'organisateur : sa charge porte le
   * rôle `control`. Son aperçu de la régie affichait donc les nombres quoi qu'il
   * coche (la case paraissait sans effet), puis, une fois l'aperçu corrigé seul, sa
   * page « En direct » les montrait encore (tâche #108) : un organisateur qui ouvre
   * le direct est servi par ce composant comme n'importe quel téléphone.
   *
   * On ne redemande pas au serveur une charge « en tant que participant » : ce
   * serait accepter un rôle dicté par le client (voir `LiveView`). La règle
   * s'applique donc à l'AFFICHAGE, sur des données qu'il a déjà le droit de voir.
   * La régie, elle, LIT ses chiffres par `Tally`, qui ne masque rien.
   */
  const hideCounts = raw.hidden || live.state.hideVoteCounts;
  const results = hideCounts
    ? { ...raw, hidden: true, totalVoters: null }
    : raw;

  /* La révélation n'est que pour le direct : la régie relit ses résultats par
     `Tally`, en barres, parce qu'elle les LIT au lieu de les mettre en scène. */
  const reveal = readReveal(activity.config, activity.type);
  const props = { activity, results, mine, numbers };
  if (reveal?.animation === "podium") return <RevealPodium {...props} reveal={reveal} />;
  if (reveal?.animation === "answer") return <RevealAnswer {...props} reveal={reveal} />;
  if (reveal?.animation === "verdict") return <RevealVerdict {...props} reveal={reveal} />;

  return (
    <Tally activity={activity} results={results} mine={mine} numbers={numbers} />
  );
}

/**
 * LES LIGNES DU TABLEAU, prêtes à dessiner : la part écrite, la bonne réponse, la
 * mienne. Partagées par les barres et le podium, pour qu'ils disent les mêmes nombres.
 */
function useRows(activity: Activity, results: ResultsData, mine: number[]) {
  const locale = useLocale();
  /* La plus choisie en tête : voir `tally.ts`. */
  const tallied = tallyRows(activity.choices, results);
  /* Sans le nombre de votants, deux « 33% » qui n'en sont pas se départagent par
     leurs décimales : voir `tally.ts`. */
  const percents = percentLabels(
    tallied.map(({ share }) => share),
    { untie: results.hidden, locale },
  );
  return tallied.map((row, i) => ({
    ...row,
    percent: percents[i],
    /* La bonne réponse n'existe dans le message QUE si le serveur l'a envoyée,
       c'est-à-dire une fois les résultats montrés. Avant, ce champ est absent. */
    right: row.choice.isRightAnswer === true,
    picked: mine.includes(row.choice.id),
  }));
}

/** « 12 réponses · 9 votants », quand les chiffres ne sont pas masqués. */
function Totals({ results, className = "hint" }: { results: ResultsData; className?: string }) {
  const t = useT();
  const total = results.totalVotes ?? 0;
  if (results.hidden) return null;
  return (
    <small className={className}>
      {total > 1
        ? t("LIVE_ANSWERS_N", "$1 réponses", [total])
        : t("LIVE_ANSWER_N", "$1 réponse", [total])}
      {typeof results.totalVoters === "number"
        ? results.totalVoters > 1
          ? t("LIVE_VOTERS_N", " · $1 votants", [results.totalVoters])
          : t("LIVE_VOTER_N", " · $1 votant", [results.totalVoters])
        : ""}
    </small>
  );
}

/**
 * LE TABLEAU LUI-MÊME, à partir d'une activité et de ses nombres.
 *
 * ⚠️ SORTI DE `Results` POUR LA RÉGIE, qui lit les résultats de n'importe quelle
 * ligne de sa liste, pas seulement ceux de l'activité à l'écran. Ce qu'elle reçoit
 * n'est pas une charge du direct : lui en fabriquer une pour passer par `Results`
 * aurait fait mentir le type. Aucun masquage ici — c'est à l'appelant de décider
 * ce qu'il a le droit de montrer, et `Results` le fait pour la salle.
 */
export function Tally({
  activity,
  results,
  mine = [],
  numbers = false,
}: {
  activity: Activity;
  results: ResultsData;
  mine?: number[];
  numbers?: boolean;
}) {
  const display = activity.config.display ?? "bars";
  const rows = useRows(activity, results, mine);

  return (
    <>
      {display === "pie" ? <Pie rows={rows} /> : null}

      <div className={`poll${display === "gauge" ? " poll--gauge" : ""}`}>
        {rows.map(({ choice, index, votes, share, percent, right, picked }) => (
          <div
            key={choice.id}
            className="poll__r"
            data-right={right ? "1" : undefined}
            data-mine={picked ? "1" : undefined}
          >
            <span>
              {numbers ? <SmsNumber index={index} /> : null}
              {choice.label}
            </span>
            <span className="poll__b">
              <i style={{ width: `${Math.round(share * 100)}%` }} />
            </span>
            <b>
              {percent}
              {typeof votes === "number" && !results.hidden
                ? ` (${votes})`
                : ""}
            </b>
          </div>
        ))}
      </div>

      <Totals results={results} />
    </>
  );
}

/**
 * LE CAMEMBERT.
 *
 * ⚠️ EN SVG, PAS EN `conic-gradient`. Le dégradé conique ne se lit par aucun
 * lecteur d'écran et ne peut porter ni titre ni description ; ici chaque part est
 * un chemin, et le graphe entier porte son résumé. Il reste décoratif : les mêmes
 * nombres sont écrits en toutes lettres juste en dessous.
 */
function Pie({
  rows,
}: {
  rows: { choice: { id: number; label: string; color: string | null }; share: number }[];
}) {
  let start = 0;
  const slices = rows.map(({ choice, share }) => {
    const from = start;
    start += share;
    return { id: choice.id, label: choice.label, color: choice.color, from, to: start };
  });

  const point = (fraction: number) => {
    const angle = 2 * Math.PI * fraction - Math.PI / 2;
    return [50 + 46 * Math.cos(angle), 50 + 46 * Math.sin(angle)];
  };

  return (
    <svg className="poll__pie" viewBox="0 0 100 100" role="img" aria-hidden="true">
      {slices.map((slice, index) => {
        const span = slice.to - slice.from;
        if (span <= 0) return null;
        /* Une part qui fait tout le tour ne se dessine pas par un arc : les deux
           extrémités se rejoignent et le chemin est vide. Un cercle plein, alors. */
        if (span >= 0.999) {
          return (
            <circle
              key={slice.id}
              cx="50"
              cy="50"
              r="46"
              fill={slice.color || `var(--gk-brand-${500 - (index % 3) * 100})`}
            />
          );
        }
        const [x1, y1] = point(slice.from);
        const [x2, y2] = point(slice.to);
        const large = span > 0.5 ? 1 : 0;
        return (
          <path
            key={slice.id}
            d={`M 50 50 L ${x1} ${y1} A 46 46 0 ${large} 1 ${x2} ${y2} Z`}
            fill={slice.color || `var(--gk-brand-${500 - (index % 3) * 100})`}
          />
        );
      })}
    </svg>
  );
}

/**
 * ═══ LE PODIUM DES RÉSULTATS ═══
 *
 * La révélation « podium » (voir `reveal.ts`) : les marches montent, puis les réponses
 * s'y dévoilent de la dernière place à la première, et la gagnante arrive avec sa
 * couronne. Le même composant sur le mur, le téléphone et l'aperçu de la régie.
 *
 * ⚠️ LES CLASSES DU CLASSEMENT (`.podium`, `.podium__c`, `.podium__b`…), PAS DES
 * NOUVELLES. Elles sont un contrat vendu au client (voir `live.css`) : une règle écrite
 * contre le podium des points vaut aussi pour celui des réponses. `.podium--rvl` porte
 * ce qui n'est qu'à lui, l'animation.
 *
 * ⚠️ LES MARCHES SONT CLÉES PAR LEUR PLACE, PAS PAR LA RÉPONSE. Les résultats se
 * relisent pendant que le vote tourne ; une réponse qui dépasse l'autre changerait de
 * nœud, et le navigateur rejouerait son entrée — elle disparaîtrait le temps de son
 * délai, puis reviendrait. Clée par sa place, la marche reste, et seul son texte change.
 */
function RevealPodium({ activity, results, reveal, mine, numbers }: RevealProps<PodiumReveal>) {
  const t = useT();
  const rows = useRows(activity, results, mine);
  const { steps, rest } = podiumOf(rows, reveal.places);

  if (!steps.length) {
    return (
      <p className="empty">
        {t("LIVE_REVEAL_NOBODY", "Personne n'a encore répondu.")}
      </p>
    );
  }

  const lowest = steps.reduce((max, step) => Math.max(max, step.rank), 1);
  const done = winnerAt(steps);

  return (
    <div
      className="rvl"
      style={
        {
          "--rv-done": `${done}s`,
          "--rv-after": `${done + TIMING.after}s`,
        } as CSSProperties
      }
    >
      {/* ⚠️ LA CLÉ DU VERDICT, PAS `LIVE_REVEAL_KICKER` (tâche #369). Le dictionnaire du
          produit garde l'ancienne phrase, « Et la réponse gagnante est… », sous l'ancienne
          clé et en six langues : elle l'emporterait sur n'importe quel texte par défaut
          écrit ici. La même phrase, la même clé — une seule traduction à tenir. */}
      <span className="rvl__k">
        {reveal.kicker || t("LIVE_REVEAL_VERDICT_KICKER", "Résultat du vote")}
      </span>

      <div
        className="podium podium--rvl"
        data-medals={reveal.medals ? "1" : undefined}
        style={{ "--rv-n": steps.length } as CSSProperties}
      >
        {steps.map(({ row, rank }, slot) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={slot}
            className="podium__c"
            data-rang={rank}
            data-win={rank === 1 ? "1" : undefined}
            data-right={row.right ? "1" : undefined}
            data-mine={row.picked ? "1" : undefined}
            style={{ "--rv-at": `${revealAt(rank, lowest)}s` } as CSSProperties}
          >
            {rank === 1 ? <Crown /> : null}
            {row.choice.image ? (
              <img className="podium__i" src={row.choice.image} alt="" />
            ) : null}
            <span className="podium__n">
              {numbers ? <SmsNumber index={row.index} /> : null}
              {row.choice.label}
            </span>
            <span className="podium__p">
              {row.percent}
              {typeof row.votes === "number" && !results.hidden ? ` (${row.votes})` : ""}
            </span>
            <span className="podium__b">{rank}</span>
          </div>
        ))}
      </div>

      {reveal.confetti ? <Confetti /> : null}

      {reveal.rest && rest.length ? (
        <ol className="rvl__rest">
          {rest.map(({ row, rank }) => (
            <li
              key={row.choice.id}
              data-right={row.right ? "1" : undefined}
              data-mine={row.picked ? "1" : undefined}
            >
              <i>{rank}</i>
              <span>
                {numbers ? <SmsNumber index={row.index} /> : null}
                {row.choice.label}
              </span>
              <b>{row.percent}</b>
            </li>
          ))}
        </ol>
      ) : null}

      <Totals results={results} className="hint rvl__end" />
    </div>
  );
}

/** Ce que reçoit chaque révélation. */
type RevealProps<R> = {
  activity: Activity;
  results: ResultsData;
  reveal: R;
  mine: number[];
  numbers: boolean;
};

/** Une durée en secondes, pour une variable CSS : deux décimales suffisent à l'œil. */
const seconds = (value: number) => `${Math.round(value * 100) / 100}s`;

/**
 * ═══ LA BONNE RÉPONSE ═══
 *
 * Toutes les réponses en tuiles, dans l'ordre où la salle les a vues pendant le vote —
 * pas triées : les numéros de SMS et les places doivent rester où on les a lus. Les
 * mauvaises s'éteignent une à une, de la moins choisie à la plus choisie ; la bonne
 * s'allume en dernier. Les parts n'arrivent qu'après : les donner avant ferait lire la
 * fin avant la fin.
 *
 * ⚠️ SANS BONNE RÉPONSE MARQUÉE, LES BARRES. Un quiz peut en être privé (on l'a créé en
 * sondage déguisé, ou on a oublié la coche) : rien ne s'allumerait, et la salle
 * regarderait toutes les tuiles s'éteindre.
 */
function RevealAnswer({ activity, results, reveal, mine, numbers }: RevealProps<AnswerReveal>) {
  const t = useT();
  const rows = useRows(activity, results, mine);
  const timeline = answerTimeline(rows);
  if (!timeline) {
    return <Tally activity={activity} results={results} mine={mine} numbers={numbers} />;
  }

  const tiles = rows.slice().sort((a, b) => a.index - b.index);

  return (
    <div
      className="rvl"
      data-anim="answer"
      style={
        {
          "--rv-done": seconds(timeline.done),
          "--rv-after": seconds(timeline.done + ANSWER_TIMING.after),
        } as CSSProperties
      }
    >
      <span className="rvl__k">
        {reveal.kicker || t("LIVE_REVEAL_ANSWER_KICKER", "La bonne réponse est…")}
      </span>

      <div
        className="rva"
        style={
          {
            "--rva-cols": tileColumns(tiles.length),
            "--rva-cols-phone": Math.min(2, tiles.length),
          } as CSSProperties
        }
      >
        {tiles.map((row, i) => (
          <div
            key={row.choice.id}
            className="rva__t"
            data-right={row.right ? "1" : undefined}
            data-mine={row.picked ? "1" : undefined}
            style={
              {
                "--rv-in": seconds(i * ANSWER_TIMING.stagger),
                "--rv-at": seconds(timeline.at.get(row.index) ?? timeline.done),
              } as CSSProperties
            }
          >
            {row.choice.image ? <img className="rva__i" src={row.choice.image} alt="" /> : null}
            <span className="rva__l">
              {numbers ? <SmsNumber index={row.index} /> : null}
              {row.choice.label}
            </span>
            <b className="rva__p">
              {row.percent}
              {typeof row.votes === "number" && !results.hidden ? ` (${row.votes})` : ""}
            </b>
            <span className="rva__b">
              <i style={{ width: `${Math.round(row.share * 100)}%` }} />
            </span>
          </div>
        ))}
      </div>

      {reveal.confetti ? <Confetti /> : null}
      <Totals results={results} className="hint rvl__end" />
    </div>
  );
}

/** Une part avec une décimale : au seuil, « 66 % » ne dit pas de quel côté on est. */
function oneDecimal(ratio: number, locale: string): string {
  const options = { minimumFractionDigits: 0, maximumFractionDigits: 1 };
  let format: Intl.NumberFormat;
  try {
    format = new Intl.NumberFormat(locale, options);
  } catch {
    format = new Intl.NumberFormat(undefined, options);
  }
  return `${format.format(ratio * 100)}%`;
}

/**
 * ═══ LE VERDICT ═══
 *
 * La part des suffrages exprimés qui vaut « pour » remplit une jauge, sous le trait du
 * seuil ; quand elle s'arrête, le tampon tombe : adoptée ou rejetée. Le détail de
 * chaque réponse suit, l'abstention marquée comme non comptée.
 *
 * ⚠️ LE VERDICT EST CALCULÉ ICI, PAS DÉCIDÉ PAR LA RÉGIE : c'est la règle réglée à la
 * préparation (seuil, abstention) appliquée aux voix. Voir `verdictOf`.
 *
 * ⚠️ UNE RÉPONSE « POUR » QUI N'EXISTE PLUS — retirée depuis — ramène aux barres
 * plutôt que de proclamer un verdict sur la mauvaise ligne.
 */
function RevealVerdict({ activity, results, reveal, mine, numbers }: RevealProps<VerdictReveal>) {
  const t = useT();
  const locale = useLocale();
  const rows = useRows(activity, results, mine);
  const pro = rows.find((row) => row.index === reveal.approve);
  if (!pro) {
    return <Tally activity={activity} results={results} mine={mine} numbers={numbers} />;
  }

  const verdict = verdictOf(rows, reveal);
  if (!verdict) {
    return (
      <p className="empty">
        {t("LIVE_VERDICT_NONE", "Aucun suffrage exprimé pour l'instant.")}
      </p>
    );
  }

  const filled = VERDICT_TIMING.start + VERDICT_TIMING.fill;
  const done = filled + VERDICT_TIMING.stamp;
  const stamp = verdict.adopted
    ? reveal.adopted || t("LIVE_VERDICT_ADOPTED", "Adoptée")
    : reveal.rejected || t("LIVE_VERDICT_REJECTED", "Rejetée");
  const bar =
    reveal.majority === "two-thirds"
      ? t("LIVE_VERDICT_TWO_THIRDS", "Deux tiers")
      : t("LIVE_VERDICT_HALF", "Majorité");
  const all = rows.slice().sort((a, b) => a.index - b.index);
  /**
   * ⚠️ LA LISTE PARLE EN SUFFRAGES EXPRIMÉS, COMME LA JAUGE. Elle donnait la part de
   * TOUS les votes : « Pour 54 % » sous une jauge à 64,5 %, deux chiffres pour la même
   * réponse. Seule l'abstention garde sa part du total, marquée non comptée.
   */
  const off = rows.find((row) => row.index === reveal.abstain);
  const expressed = 1 - (off?.share ?? 0);
  const shareOf = (row: (typeof rows)[number]) =>
    row === off || expressed <= 0 ? row.share : row.share / expressed;

  return (
    <div
      className="rvl"
      data-anim="verdict"
      style={
        {
          "--rv-done": seconds(done),
          "--rv-after": seconds(done + VERDICT_TIMING.after),
        } as CSSProperties
      }
    >
      <span className="rvl__k">
        {reveal.kicker || t("LIVE_REVEAL_VERDICT_KICKER", "Résultat du vote")}
      </span>

      <div
        className="rvv"
        data-adopted={verdict.adopted ? "1" : "0"}
        style={
          {
            "--rv-share": verdict.ratio,
            "--rv-bar": verdict.threshold,
            "--rv-start": seconds(VERDICT_TIMING.start),
            "--rv-fill": seconds(VERDICT_TIMING.fill),
            "--rv-at": seconds(filled),
          } as CSSProperties
        }
      >
        <div className="rvv__h">
          <span className="rvv__l">{pro.choice.label}</span>
          <b className="rvv__r">{oneDecimal(verdict.ratio, locale)}</b>
        </div>
        <div className="rvv__g">
          <i className="rvv__f" />
          <span className="rvv__m">
            <span>{bar}</span>
          </span>
        </div>
        <small className="rvv__x">
          {t("LIVE_VERDICT_EXPRESSED", "des suffrages exprimés")}
        </small>
        <span className="rvv__s">{stamp}</span>
      </div>

      {reveal.confetti && verdict.adopted ? <Confetti /> : null}

      <ol className="rvl__rest rvl__rest--plain">
        {all.map((row) => (
          <li
            key={row.choice.id}
            data-mine={row.picked ? "1" : undefined}
            data-off={row.index === reveal.abstain ? "1" : undefined}
          >
            <span>
              {numbers ? <SmsNumber index={row.index} /> : null}
              {row.choice.label}
            </span>
            <b>
              {oneDecimal(shareOf(row), locale)}
              {typeof row.votes === "number" && !results.hidden ? ` (${row.votes})` : ""}
            </b>
            {row.index === reveal.abstain ? (
              <small>{t("LIVE_VERDICT_NOT_COUNTED", "non comptée")}</small>
            ) : null}
          </li>
        ))}
      </ol>

      <Totals results={results} className="hint rvl__end" />
    </div>
  );
}

/** La couronne du gagnant. Décorative : le rang est écrit sur la marche. */
function Crown() {
  return (
    <svg className="podium__cr" viewBox="0 0 24 18" aria-hidden="true">
      <path
        d="M2 5.5 7 10l5-8 5 8 5-4.5-2 10.5H4z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Tirée une fois pour toutes : la même pluie sur le mur et dans l'aperçu. */
const PIECES = confetti(32);

/**
 * LA PLUIE DE CONFETTIS, au moment où le gagnant se dévoile. Rien que du CSS, calé sur
 * `--rv-done` ; immobile et masquée si l'on a demandé moins d'animation.
 */
function Confetti() {
  return (
    <div className="rvl__fx" aria-hidden="true">
      {PIECES.map((piece, i) => (
        <i
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          data-tint={piece.tint}
          style={
            {
              "--x": `${piece.x}%`,
              "--dx": `${piece.drift}cqw`,
              "--spin": `${piece.spin}turn`,
              "--fall": `${piece.fall}s`,
              "--delay": `${piece.delay}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
